import { useEffect, useMemo, useState } from "react";
import { api, formatUSD, API, getExpositorToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Building2, Upload, CheckCircle2, Receipt, CreditCard, Send, ExternalLink,
  Plus, Minus, Loader2, Layers, Ruler, BadgeCheck, HelpCircle, FileDown,
  Sparkles, Check, Tv, Armchair, Lightbulb, Image as ImageIcon, Package
} from "lucide-react";
import { LED_COLORS, CARPET_COLORS, STATUS_LABEL, BRAND, PRESTACIONES_BASE, TV_EXTRA_OPTIONS, TIPO_UBICACION_LABEL } from "@/lib/constants";

const inputCls = "bg-zinc-950 border-zinc-800 focus:border-[#FF5A00] focus-visible:ring-[#FF5A00]/30 rounded-sm";

const Saver = ({ onSave, saving, label = "Guardar" }) => (
  <Button data-testid="save-section-btn" onClick={onSave} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" />{label}</>}
  </Button>
);

const SectionTitle = ({ kicker, title, desc }) => (
  <div className="mb-8">
    <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF5A00] mb-2">{kicker}</div>
    <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h2>
    {desc && <p className="text-sm text-zinc-400 mt-2 max-w-2xl">{desc}</p>}
  </div>
);

const Field = ({ label, children, hint, required }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
      {label} {required && <span className="text-[#FF5A00]">*</span>}
    </Label>
    {children}
    {hint && <div className="text-xs text-zinc-500 mt-1.5">{hint}</div>}
  </div>
);

// -------------------- 1. Bienvenida --------------------
export function StepBienvenida({ stand, setStep }) {
  return (
    <div>
      <SectionTitle
        kicker="Bienvenida"
        title={`Hola${stand.company ? `, ${stand.company}` : ""}`}
        desc="Detectamos el espacio contratado asociado a tu número de stand. A continuación vas a poder configurar el Paquete Full Equipado y seleccionar upgrades opcionales."
      />

      <Alert className="bg-zinc-900 border-zinc-800 rounded-sm mb-6">
        <AlertDescription className="text-xs text-zinc-300 leading-relaxed">
          Este configurador es para expositores que <b>ya contrataron</b> su espacio en Argentina Mining Norte 2026.
          No se venden ni reservan espacios acá. Sólo configurás el armado con el Paquete Full Equipado y upgrades opcionales.
        </AlertDescription>
      </Alert>

      <div className="grid lg:grid-cols-5 gap-6 mb-8">
        <div className="lg:col-span-3 space-y-4">
          {stand.combo_members?.length > 1 && (
            <Alert data-testid="combo-info-alert" className="bg-[#FF5A00]/5 border-[#FF5A00]/30 rounded-sm">
              <AlertDescription className="text-xs text-zinc-200">
                <b className="text-[#FF5A00]">Configuración conjunta:</b> stands {stand.combo_members.join(" + ")} unidos en un único Paquete Full Equipado de {fmtNum(stand.ancho)} × {fmtNum(stand.profundidad)} m.
                Las paredes internas compartidas fueron eliminadas. El mobiliario, la TV y demás prestaciones base no se multiplican por la cantidad de espacios.
              </AlertDescription>
            </Alert>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <InfoCard icon={<Layers className="w-4 h-4" />} label="Stand asignado" value={stand.combo_members?.length > 1 ? stand.combo_members.map((c) => `#${c}`).join(" + ") : `#${stand.stand_number}`} />
            <InfoCard icon={<Building2 className="w-4 h-4" />} label="Empresa" value={stand.company || "A completar"} />
            <InfoCard icon={<Ruler className="w-4 h-4" />} label="Medida" value={`${fmtNum(stand.ancho)} × ${fmtNum(stand.profundidad)} m · ${fmtNum(stand.superficie)} m²`} />
            <InfoCard icon={<BadgeCheck className="w-4 h-4" />} label="Tipo de ubicación" value={TIPO_UBICACION_LABEL[stand.tipo_ubicacion] || "A definir"} />
          </div>

          {stand.is_special ? (
            <Alert className="bg-amber-500/5 border-amber-500/30 rounded-sm">
              <AlertDescription className="text-xs text-amber-200">
                Este espacio es <b>especial</b> ({stand.superficie} m²) y requiere validación de Servicios Oficiales para asignar el Paquete Full Equipado. Consultá a Rocky (burbuja de chat) o contactá a Servicios Oficiales para coordinar.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="border border-[#FF5A00]/30 bg-[#FF5A00]/5 rounded-sm p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] mb-2">Valor Paquete Full Equipado</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-mono font-semibold text-white">USD {stand.base_price ? Math.round(stand.base_price).toLocaleString("es-AR") : "—"}</span>
                  <span className="text-xs text-zinc-400">+ IVA</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">Corresponde a la ubicación · {TIPO_UBICACION_LABEL[stand.tipo_ubicacion] || ""}</div>
              </div>
              {stand.stand_number?.startsWith("R") && (
                <Alert className="bg-amber-500/5 border-amber-500/30 rounded-sm">
                  <AlertDescription className="text-xs text-amber-200">
                    <b>Sector Rotonda:</b> altura máxima permitida <b>2,40 m</b>.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Paquete Full Equipado incluye</div>
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {PRESTACIONES_BASE.map((i) => (
                  <li key={i} className="flex items-start text-sm text-zinc-200">
                    <CheckCircle2 className="w-4 h-4 text-[#FF5A00] mr-2 shrink-0 mt-0.5" /> {i}
                  </li>
                ))}
              </ul>
              <div className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
                Todos los valores están sujetos a disponibilidad operativa, especialmente pantallas TV LED.
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <div className="relative rounded-sm overflow-hidden border border-zinc-800">
            <img src={BRAND.STAND_PHOTO} alt="Ejemplo Paquete Full Equipado" className="w-full h-64 lg:h-full object-cover" />
            <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider bg-zinc-950/80 px-2 py-1 rounded-sm border border-zinc-800">
              Imagen de referencia
            </div>
          </div>
        </div>
      </div>

      <Button data-testid="start-config-btn" onClick={() => setStep(2)} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-11 px-6">
        Comenzar configuración
      </Button>
    </div>
  );
}

const InfoCard = ({ icon, label, value }) => (
  <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 mb-2">{icon}{label}</div>
    <div className="text-base font-medium text-white">{value}</div>
  </div>
);

const fmtNum = (n) => (n == null ? "—" : String(n).replace(".", ","));
const labelType = (t) => ({ frontal: "Frontal", esquina: "Esquina", isla: "Isla", personalizado: "Personalizado" }[t] || t);

// -------------------- 2. Datos + Facturación --------------------
export function StepDatos({ stand, reload, locked }) {
  const [form, setForm] = useState({ ...stand.configuration.datos_facturacion });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/expositor/stand/${stand.stand_number}/config`, { section: "datos_facturacion", data: form });
      toast.success("Datos guardados");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div>
      <SectionTitle kicker="Paso 2" title="Datos del expositor y facturación" desc="Unificamos contacto y datos administrativos en un solo paso." />
      <fieldset disabled={locked} className="space-y-6 max-w-3xl">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Contacto</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Empresa" required><Input data-testid="input-empresa" className={inputCls} value={form.empresa || ""} onChange={(e) => set("empresa", e.target.value)} /></Field>
            <Field label="Nombre comercial"><Input data-testid="input-nombre-comercial" className={inputCls} value={form.nombre_comercial || ""} onChange={(e) => set("nombre_comercial", e.target.value)} /></Field>
            <Field label="Responsable" required><Input data-testid="input-responsable" className={inputCls} value={form.responsable || ""} onChange={(e) => set("responsable", e.target.value)} /></Field>
            <Field label="Email" required><Input data-testid="input-email" type="email" className={inputCls} value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="WhatsApp" required><Input data-testid="input-whatsapp" className={inputCls} value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+54 9 ..." /></Field>
          </div>
        </div>
        <Separator className="bg-zinc-900" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Facturación</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Razón social"><Input data-testid="fac-razon" className={inputCls} value={form.razon_social || ""} onChange={(e) => set("razon_social", e.target.value)} /></Field>
            <Field label="CUIT"><Input data-testid="fac-cuit" className={inputCls} value={form.cuit || ""} onChange={(e) => set("cuit", e.target.value)} /></Field>
            <Field label="Condición frente al IVA"><Input data-testid="fac-iva" className={inputCls} placeholder="Resp. Inscripto / Monotributo / Exento" value={form.condicion_iva || ""} onChange={(e) => set("condicion_iva", e.target.value)} /></Field>
            <Field label="Domicilio fiscal"><Input data-testid="fac-domicilio" className={inputCls} value={form.domicilio || ""} onChange={(e) => set("domicilio", e.target.value)} /></Field>
            <Field label="Email para factura"><Input data-testid="fac-email" type="email" className={inputCls} value={form.email_factura || ""} onChange={(e) => set("email_factura", e.target.value)} /></Field>
            <Field label="Orden de compra interna (opcional)"><Input data-testid="fac-oc" className={inputCls} value={form.orden_compra || ""} onChange={(e) => set("orden_compra", e.target.value)} /></Field>
          </div>
          <div className="mt-4">
            <Field label="Observaciones"><Textarea data-testid="fac-obs" className={inputCls} rows={2} value={form.observaciones || ""} onChange={(e) => set("observaciones", e.target.value)} /></Field>
          </div>
        </div>
        <Saver onSave={save} saving={saving} />
      </fieldset>
    </div>
  );
}

// -------------------- 3. Logo + Diseño --------------------
export function StepDiseno({ stand, reload, locked }) {
  const [logoForm, setLogoForm] = useState({ ...stand.configuration.logo });
  const [disenoForm, setDisenoForm] = useState({ ...stand.configuration.diseno });
  const [saving, setSaving] = useState(false);

  const updatePanel = (i, field, value) => {
    const paneles = [...disenoForm.paneles];
    paneles[i] = { ...paneles[i], [field]: value };
    setDisenoForm({ ...disenoForm, paneles });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/expositor/stand/${stand.stand_number}/config`, { section: "logo", data: logoForm });
      await api.post(`/expositor/stand/${stand.stand_number}/config`, { section: "diseno", data: disenoForm });
      toast.success("Logo y diseño guardados");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <SectionTitle kicker="Paso 3" title="Logo y diseño gráfico" desc="Podés usar una plantilla estándar con tu logo o subir una gráfica personalizada por panel." />
      <fieldset disabled={locked} className="space-y-8">

        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-6 space-y-4">
            <div className="text-sm font-semibold">Logo</div>
            <Alert className="bg-zinc-950 border-zinc-800 rounded-sm">
              <AlertDescription className="text-sm text-zinc-300">
                Para una correcta producción, el logo debe enviarse preferentemente en <span className="font-mono text-[#FF5A00]">PDF editable a curvas</span>. También aceptamos AI · EPS · SVG.
              </AlertDescription>
            </Alert>
            <Field label="Link al archivo (Drive / WeTransfer / Dropbox)">
              <Input data-testid="input-logo-link" className={inputCls} placeholder="https://..." value={logoForm.link || ""} onChange={(e) => setLogoForm({ ...logoForm, link: e.target.value })} />
            </Field>
            <div className="flex items-start gap-3 p-3 border border-zinc-800 rounded-sm bg-zinc-950">
              <Checkbox
                data-testid="checkbox-logo-assistance"
                id="assist"
                checked={!!logoForm.requires_assistance}
                onCheckedChange={(c) => setLogoForm({ ...logoForm, requires_assistance: !!c })}
              />
              <label htmlFor="assist" className="text-sm leading-relaxed cursor-pointer">
                <span className="text-white font-medium block">No tengo logo vectorial / necesito asistencia</span>
                <span className="text-zinc-500 text-xs">Se marcará como “requiere asistencia de diseño”.</span>
              </label>
            </div>
          </CardContent>
        </Card>

        <div>
          <div className="text-sm font-semibold mb-3">Opción de diseño gráfico</div>
          <RadioGroup value={disenoForm.opcion} onValueChange={(v) => setDisenoForm({ ...disenoForm, opcion: v })} className="grid md:grid-cols-2 gap-4">
            <OptionCard testid="diseno-opt-a" id="A" value={disenoForm.opcion} title="A · Diseño estándar prearmado" desc="Aplicamos tu logo sobre una plantilla gráfica estándar del Paquete Full Equipado." />
            <OptionCard testid="diseno-opt-b" id="B" value={disenoForm.opcion} title="B · Gráfica personalizada por panel" desc="Subís tu propia gráfica para cada panel del espacio." />
          </RadioGroup>
        </div>

        {disenoForm.opcion === "A" && (
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
            <Field label="Slogan (opcional)"><Input data-testid="input-slogan" className={inputCls} value={disenoForm.slogan || ""} onChange={(e) => setDisenoForm({ ...disenoForm, slogan: e.target.value })} /></Field>
            <Field label="Web (opcional)"><Input data-testid="input-web" className={inputCls} value={disenoForm.web || ""} onChange={(e) => setDisenoForm({ ...disenoForm, web: e.target.value })} /></Field>
            <Field label="Redes (opcional)"><Input data-testid="input-redes" className={inputCls} value={disenoForm.redes || ""} onChange={(e) => setDisenoForm({ ...disenoForm, redes: e.target.value })} /></Field>
            <Field label="QR / Link (opcional)"><Input data-testid="input-qr" className={inputCls} value={disenoForm.qr_link || ""} onChange={(e) => setDisenoForm({ ...disenoForm, qr_link: e.target.value })} /></Field>
            <div className="sm:col-span-2">
              <Field label="Observaciones de marca"><Textarea data-testid="input-obs-marca" className={inputCls} rows={2} value={disenoForm.observaciones_marca || ""} onChange={(e) => setDisenoForm({ ...disenoForm, observaciones_marca: e.target.value })} /></Field>
            </div>
          </div>
        )}

        {disenoForm.opcion === "B" && (
          <div className="space-y-4">
            {/* Imagen de referencia según tipo de stand */}
            {stand.tipo_ubicacion === "sin_definir" || (!stand.tipo_ubicacion && !stand.is_special) ? (
              <Alert className="bg-amber-500/5 border-amber-500/30 rounded-sm">
                <AlertDescription className="text-sm text-amber-200">
                  No pudimos identificar la configuración de tu stand. Consultá a Rocky (burbuja de chat) o contactá a Servicios Oficiales para continuar con la carga de gráficas.
                </AlertDescription>
              </Alert>
            ) : stand.tipo_ubicacion === "isla_0p" ? (
              <Alert className="bg-zinc-900 border-zinc-800 rounded-sm">
                <AlertDescription className="text-sm text-zinc-300">
                  Tu espacio es <span className="font-semibold text-white">isla / sin paredes</span>. No se solicita gráfica de pared. Los elementos gráficos especiales de tu paquete se coordinarán con Servicios Oficiales.
                </AlertDescription>
              </Alert>
            ) : (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Referencia de paneles para tu stand</div>
                <div className="rounded-sm overflow-hidden border border-zinc-800 bg-zinc-900">
                  <img
                    src={(stand.tipo_ubicacion === "esquina_2p" || stand.tipo_ubicacion === "peninsula_1p") ? BRAND.REF_STAND_2P : BRAND.REF_STAND_3P}
                    alt="Referencia de paneles"
                    className="w-full h-auto max-h-72 object-contain bg-zinc-950"
                  />
                </div>
                <div className="text-xs text-zinc-400 mt-2">Cargá cada gráfica en el panel correspondiente según la referencia.</div>
              </div>
            )}

            {(disenoForm.paneles || []).map((p, i) => (
              <Card key={p.name || i} className="bg-zinc-900 border-zinc-800 rounded-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-base font-semibold">{p.name}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-0.5">{p.size}</div>
                    </div>
                    <Badge variant="outline" className="border-zinc-700 text-zinc-400 rounded-sm">Panel {i + 1}</Badge>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Link del archivo (gráfica)"><Input data-testid={`panel-${i}-link`} className={inputCls} placeholder="https://..." value={p.link || ""} onChange={(e) => updatePanel(i, "link", e.target.value)} /></Field>
                    <Field label="Observaciones"><Input data-testid={`panel-${i}-obs`} className={inputCls} value={p.observaciones || ""} onChange={(e) => updatePanel(i, "observaciones", e.target.value)} /></Field>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Saver onSave={save} saving={saving} />
      </fieldset>
    </div>
  );
}

const OptionCard = ({ testid, id, value, title, desc }) => {
  const active = value === id;
  return (
    <label data-testid={testid} className={`block cursor-pointer rounded-sm border p-5 transition ${active ? "border-[#FF5A00] bg-[#FF5A00]/5" : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"}`}>
      <div className="flex items-start gap-3">
        <RadioGroupItem value={id} id={`opt-${id}-${testid}`} className="mt-1 border-zinc-600 text-[#FF5A00]" />
        <div>
          <div className="text-base font-semibold">{title}</div>
          <div className="text-sm text-zinc-400 mt-1">{desc}</div>
        </div>
      </div>
    </label>
  );
};

// -------------------- 4. Terminaciones + TV --------------------
export function StepTerminaciones({ stand, reload, locked }) {
  const [term, setTerm] = useState({ ...stand.configuration.terminaciones });
  const [tv, setTv] = useState({ ...stand.configuration.tv });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/expositor/stand/${stand.stand_number}/config`, { section: "terminaciones", data: term });
      await api.post(`/expositor/stand/${stand.stand_number}/config`, { section: "tv", data: tv });
      toast.success("Terminaciones y TV guardados");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <SectionTitle kicker="Paso 4" title="Terminaciones y contenido TV" desc="Colores sujetos a disponibilidad operativa." />
      <fieldset disabled={locked} className="space-y-8">
        <div>
          <div className="text-sm font-medium mb-3">Color de tira LED inferior</div>
          <div className="flex flex-wrap gap-3">
            {LED_COLORS.map((c) => {
              const active = term.led_color === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`led-color-${c.id.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setTerm({ ...term, led_color: c.id })}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm border transition ${active ? "border-[#FF5A00] bg-[#FF5A00]/5" : "border-zinc-800 hover:border-zinc-700"}`}
                  type="button"
                >
                  <span className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: c.hex, boxShadow: `0 0 12px ${c.hex}99` }} />
                  <span className="text-sm">{c.id}</span>
                </button>
              );
            })}
          </div>
        </div>
        <Separator className="bg-zinc-900" />
        <div>
          <div className="text-sm font-medium mb-3">Color de alfombra</div>
          <div className="flex flex-wrap gap-3">
            {CARPET_COLORS.map((c) => {
              const active = term.alfombra_color === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`carpet-color-${c.id.toLowerCase()}`}
                  onClick={() => setTerm({ ...term, alfombra_color: c.id })}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm border transition ${active ? "border-[#FF5A00] bg-[#FF5A00]/5" : "border-zinc-800 hover:border-zinc-700"}`}
                  type="button"
                >
                  <span className="w-6 h-6 rounded-sm border border-white/20" style={{ backgroundColor: c.hex }} />
                  <span className="text-sm">{c.id}</span>
                </button>
              );
            })}
          </div>
        </div>
        <Separator className="bg-zinc-900" />
        <div>
          <div className="text-sm font-medium mb-3">Contenido para TV LED 42″ (incluida)</div>
          <RadioGroup value={tv.opcion} onValueChange={(v) => setTv({ ...tv, opcion: v })} className="grid md:grid-cols-3 gap-3">
            <OptionCard testid="tv-opt-a" id="A" value={tv.opcion} title="A · Subo mi video institucional" desc="MP4, 16:9, 1920×1080, en loop." />
            <OptionCard testid="tv-opt-b" id="B" value={tv.opcion} title="B · Usar logo animado en TV LED" desc="Si no tenés video, usamos tu logo animado en pantalla." />
            <OptionCard testid="tv-opt-c" id="C" value={tv.opcion} title="C · Necesito asistencia AV" desc="Edición, conversión o adaptación de tu contenido." />
          </RadioGroup>

          {tv.opcion === "A" && (
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mt-4">
              <Field label="Link Drive / WeTransfer / Dropbox" hint="Formato recomendado: MP4, 16:9, 1920 × 1080, en loop.">
                <Input data-testid="tv-link" className={inputCls} value={tv.link || ""} onChange={(e) => setTv({ ...tv, link: e.target.value })} placeholder="https://..." />
              </Field>
              <Field label="Observaciones">
                <Input data-testid="tv-obs" className={inputCls} value={tv.observaciones || ""} onChange={(e) => setTv({ ...tv, observaciones: e.target.value })} />
              </Field>
            </div>
          )}
        </div>
        <Saver onSave={save} saving={saving} />
      </fieldset>
    </div>
  );
}

// -------------------- 5. Upgrades --------------------
const normalizeCart = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.items)) return raw.items;
  return [];
};

export function StepUpgrades({ stand, reload, locked }) {
  const [catalog, setCatalog] = useState([]);
  const rawUp = stand.configuration.upgrades || {};
  const [cart, setCart] = useState(normalizeCart(rawUp));
  const [tvExtras, setTvExtras] = useState(rawUp.tv_extras || []);
  const [otros, setOtros] = useState(rawUp.otros_request || "");
  const [saving, setSaving] = useState(false);
  const [elementos, setElementos] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [tvBanReason, setTvBanReason] = useState(null);
  const [elemBusy, setElemBusy] = useState(null);

  useEffect(() => { api.get("/catalog/upgrades").then((r) => setCatalog(r.data)); }, []);
  useEffect(() => {
    if (!stand.full_equipado) return;
    api.get(`/expositor/stand/${stand.stand_number}/elementos`).then(({ data }) => {
      setElementos(data.elementos || []);
      setPricing(data.pricing || null);
      setTvBanReason(data.tv_ban_reason || null);
    }).catch(() => {});
  }, [stand.stand_number, stand.full_equipado]);

  const updateElementQty = async (item_id, current_qty) => {
    setElemBusy(item_id);
    try {
      const { data } = await api.patch(`/expositor/stand/${stand.stand_number}/elementos/${item_id}`, { current_qty });
      setElementos(data.elementos || []);
      setPricing(data.pricing || null);
      setTvBanReason(data.tv_ban_reason || null);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); }
    finally { setElemBusy(null); }
  };
  const restoreElement = async (item_id) => {
    setElemBusy(item_id);
    try {
      const { data } = await api.post(`/expositor/stand/${stand.stand_number}/elementos/${item_id}/restore`);
      setElementos(data.elementos || []);
      setPricing(data.pricing || null);
      setTvBanReason(data.tv_ban_reason || null);
      toast.success("Cantidad original restaurada");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo restaurar"); }
    finally { setElemBusy(null); }
  };

  const qtyOf = (id) => cart.find((c) => c.upgrade_id === id)?.quantity || 0;

  const setQty = (item, qty) => {
    let next = cart.filter((c) => c.upgrade_id !== item.id);
    // TV upgrades son excluyentes entre sí
    if (item.exclusive_group === "tv_upgrade" && qty > 0) {
      next = next.filter((c) => {
        const other = catalog.find((x) => x.id === c.upgrade_id);
        return other?.exclusive_group !== "tv_upgrade";
      });
    }
    if (qty > 0) {
      next.push({
        upgrade_id: item.id, name: item.name, quantity: qty,
        unit_price: item.unit_price, iva: item.iva, currency: item.currency || "USD",
        exclusive_group: item.exclusive_group || null,
        subtotal: item.unit_price * qty,
      });
    }
    setCart(next);
  };

  // TV Extras (pedido adicional)
  const addTvExtra = () => setTvExtras([...tvExtras, { size: "50\"", price: 230, quantity: 1 }]);
  const updateTvExtra = (i, patch) => setTvExtras(tvExtras.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const removeTvExtra = (i) => setTvExtras(tvExtras.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    const upSub = cart.reduce((s, c) => s + c.unit_price * c.quantity, 0);
    const tvSub = tvExtras.reduce((s, t) => s + (t.price || 0) * (t.quantity || 0), 0);
    const subtotal = upSub + tvSub;
    const iva = subtotal * 0.21;
    return { subtotal, iva, total: subtotal + iva };
  }, [cart, tvExtras]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        items: cart, tv_extras: tvExtras,
        subtotal: totals.subtotal, iva: totals.iva, total: totals.total,
        currency: "USD", otros_request: otros,
      };
      await api.post(`/expositor/stand/${stand.stand_number}/config`, { section: "upgrades", data: payload });
      toast.success("Upgrades guardados");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const tvUpgrades = catalog.filter((c) => c.exclusive_group === "tv_upgrade");
  const generalUpgrades = catalog.filter((c) => c.exclusive_group !== "tv_upgrade");
  const itemCount = cart.reduce((s, c) => s + c.quantity, 0) + tvExtras.reduce((s, t) => s + (t.quantity || 0), 0);
  const totalPedido = ((stand.base_price || 0) + totals.subtotal) * 1.21;

  const iconFor = (it) => {
    const t = `${it.category || ""} ${it.name || ""}`.toLowerCase();
    if (t.includes("tv") || t.includes("pantalla") || t.includes("video")) return Tv;
    if (t.includes("mobil") || t.includes("silla") || t.includes("mesa") || t.includes("banqueta") || t.includes("sillón")) return Armchair;
    if (t.includes("luz") || t.includes("ilumin") || t.includes("led") || t.includes("neón") || t.includes("neon")) return Lightbulb;
    if (t.includes("gráfic") || t.includes("grafic") || t.includes("impres") || t.includes("logo") || t.includes("vinilo")) return ImageIcon;
    return Package;
  };

  return (
    <div>
      {/* Encabezado premium UPGRADES */}
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Paso 5 · Personalizá tu stand</div>
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-sm bg-[#FF5A00]/10 border border-[#FF5A00]/50 grid place-items-center upgrades-glow shrink-0">
            <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-[#FF5A00]" />
          </div>
          <div>
            <h2 data-testid="upgrades-title" className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#FF5A00]">UPGRADES</h2>
            <p className="text-sm text-zinc-400 mt-1.5 max-w-xl">
              Adicionales opcionales para potenciar tu Paquete Full Equipado. Valores en USD + IVA, sujetos a disponibilidad.
            </p>
          </div>
        </div>
      </div>

      <fieldset disabled={locked} className="space-y-10">

        {/* PERSONALIZÁ LOS ELEMENTOS INCLUIDOS */}
        {stand.full_equipado && elementos.length > 0 && (
          <div data-testid="incl-elements-section">
            <div className="flex items-baseline gap-3 mb-3">
              <div className="text-base font-semibold flex items-center gap-2"><Package className="w-4 h-4 text-[#FF5A00]" /> Personalizá los elementos incluidos</div>
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px] rounded-sm">Restá lo que no necesitás y llevate el descuento</Badge>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
              Los elementos del paquete pueden ajustarse. Cada unidad retirada genera un descuento automático sobre el neto antes de IVA.
            </p>
            {tvBanReason && (
              <Alert className="bg-amber-500/5 border-amber-500/40 rounded-sm mb-4">
                <AlertDescription className="text-xs text-amber-100">{tvBanReason}</AlertDescription>
              </Alert>
            )}
            <div className="grid gap-3">
              {elementos.filter((e) => e.visible_to_client && e.removable).map((e) => {
                const removed = Math.max(0, (e.included_qty || 0) - (e.current_qty || 0));
                const canDec = (e.current_qty > (e.min_qty || 0)) && !elemBusy;
                const canInc = (e.current_qty < e.included_qty) && !elemBusy;
                const disc = removed > 0 ? (
                  e.discount_type === "FIXED_TOTAL" && e.current_qty <= 0 ? e.discount_unit :
                  ((e.discount_type === "FIXED_PER_UNIT" || e.discount_type === "FIXED_PER_LINEAR_METER" || e.discount_type === "FIXED_PER_SQUARE_METER") ? removed * e.discount_unit : 0)
                ) : 0;
                return (
                  <Card key={e.item_id} data-testid={`elem-${e.item_id}`} className="bg-zinc-900 border-zinc-800 rounded-sm">
                    <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">{e.name}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {e.category} · Original {e.included_qty}{e.unit} · Vigente <span className={`font-mono ${e.current_qty < e.included_qty ? "text-amber-300" : "text-white"}`}>{e.current_qty}{e.unit}</span>
                          {e.discount_unit > 0 && (
                            <span className="text-zinc-500"> · Ahorro por {e.unit === "ml" ? "metro" : "unidad"}: <span className="text-emerald-400 font-mono">USD {e.discount_unit}</span></span>
                          )}
                        </div>
                        {removed > 0 && (
                          <div className="text-[11px] text-emerald-400 mt-1 font-mono" data-testid={`elem-desc-${e.item_id}`}>
                            − USD {disc.toLocaleString()} de descuento por {removed}{e.unit} retirado{removed > 1 ? "s" : ""}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" variant="outline" data-testid={`elem-dec-${e.item_id}`}
                          className="border-zinc-800 h-9 w-9 p-0 rounded-sm hover:bg-zinc-800"
                          disabled={!canDec}
                          onClick={() => updateElementQty(e.item_id, (e.current_qty || 0) - 1)}
                        >−</Button>
                        <div className="w-10 text-center font-mono text-sm" data-testid={`elem-qty-${e.item_id}`}>{e.current_qty}</div>
                        <Button size="sm" variant="outline" data-testid={`elem-inc-${e.item_id}`}
                          className="border-zinc-800 h-9 w-9 p-0 rounded-sm hover:bg-zinc-800"
                          disabled={!canInc}
                          onClick={() => updateElementQty(e.item_id, (e.current_qty || 0) + 1)}
                        >+</Button>
                        {e.current_qty > 0 && (
                          <Button size="sm" variant="outline" data-testid={`elem-remove-${e.item_id}`}
                            className="border-red-500/30 text-red-300 hover:bg-red-500/10 h-9 rounded-sm text-[11px] ml-2"
                            disabled={!!elemBusy}
                            onClick={() => updateElementQty(e.item_id, e.min_qty || 0)}
                          >Quitar</Button>
                        )}
                        {e.current_qty < e.included_qty && (
                          <Button size="sm" variant="outline" data-testid={`elem-restore-${e.item_id}`}
                            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 h-9 rounded-sm text-[11px]"
                            disabled={!!elemBusy}
                            onClick={() => restoreElement(e.item_id)}
                          >Restaurar</Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {pricing && pricing.descuentos_total > 0 && (
              <div className="mt-4 p-3 rounded-sm border border-emerald-500/30 bg-emerald-500/5 text-xs flex items-center justify-between" data-testid="elem-summary">
                <span className="text-emerald-200">Descuento total por elementos retirados</span>
                <span className="font-mono text-emerald-300">− USD {pricing.descuentos_total.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {stand.full_equipado && elementos.length > 0 && <Separator className="bg-zinc-900" />}

        {/* Upgrade de TV incluida (excluyente) */}
        <div>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="text-base font-semibold flex items-center gap-2"><Tv className="w-4 h-4 text-[#FF5A00]" /> Upgrade de tamaño de TV incluida</div>
            <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px] rounded-sm">Reemplaza la TV 42″ del paquete</Badge>
          </div>
          <p className="text-xs text-zinc-500 mb-4">Sólo podés seleccionar un upgrade de tamaño para la TV incluida.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {tvUpgrades.map((it) => {
              const q = qtyOf(it.id);
              const anyTvActive = cart.some((c) => c.exclusive_group === "tv_upgrade");
              const isSelected = q > 0;
              const isDisabled = (!isSelected && anyTvActive) || (!isSelected && !!tvBanReason);
              return (
                <button
                  type="button"
                  key={it.id}
                  data-testid={`tv-upgrade-${it.id}`}
                  onClick={() => setQty(it, isSelected ? 0 : 1)}
                  disabled={isDisabled}
                  className={`upgrade-card relative text-left rounded-sm border p-4 ${isSelected ? "upgrade-selected border-[#FF5A00] bg-[#FF5A00]/10" : isDisabled ? "border-zinc-900 bg-zinc-950 opacity-40 cursor-not-allowed" : "border-zinc-800 bg-zinc-900 hover:border-[#FF5A00]/50"}`}
                >
                  {isSelected && (
                    <span className="upgrade-check absolute top-2 right-2 w-5 h-5 rounded-full bg-[#FF5A00] grid place-items-center">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                  )}
                  <Tv className={`w-5 h-5 mb-2 ${isSelected ? "text-[#FF5A00]" : "text-zinc-500"}`} />
                  <div className="text-base font-semibold">{it.name.replace("Upgrade TV LED 42\" a ", "")}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Reemplazo TV 42″</div>
                  <div className="mt-2 font-mono text-[#FF5A00]">{formatUSD(it.unit_price)}<span className="text-[10px] text-zinc-500 ml-1">+ IVA</span></div>
                </button>
              );
            })}
          </div>
        </div>

        <Separator className="bg-zinc-900" />

        {/* TV LED extra (pedido adicional) */}
        <div>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="text-sm font-semibold">TV LED extra — pedido adicional</div>
            <Badge variant="outline" className="border-[#0F766E]/50 text-[#5EEAD4] text-[10px] rounded-sm">Adicional a la TV incluida</Badge>
          </div>
          <p className="text-xs text-zinc-500 mb-4">Pantallas extras que se suman a la TV incluida. Sujeto a disponibilidad.</p>
          <div className="space-y-3">
            {tvExtras.map((t, i) => (
              <Card key={`tv-${i}-${t.size}`} className="bg-zinc-900 border-zinc-800 rounded-sm">
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Tamaño</Label>
                    <select
                      data-testid={`tv-extra-size-${i}`}
                      className={`${inputCls} h-10 w-full px-2`}
                      value={t.size}
                      onChange={(e) => {
                        const opt = TV_EXTRA_OPTIONS.find(o => o.size === e.target.value);
                        updateTvExtra(i, { size: opt.size, price: opt.price });
                      }}
                    >
                      {TV_EXTRA_OPTIONS.map((o) => <option key={o.size} value={o.size}>{o.size} — USD {o.price}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Cantidad</Label>
                    <Input data-testid={`tv-extra-qty-${i}`} type="number" min="1" className={inputCls} value={t.quantity} onChange={(e) => updateTvExtra(i, { quantity: Math.max(1, Number(e.target.value) || 1) })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Precio unit.</Label>
                    <div className="h-10 flex items-center font-mono text-[#FF5A00]">{formatUSD(t.price)}</div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Subtotal</Label>
                    <div className="h-10 flex items-center font-mono text-white">{formatUSD(t.price * t.quantity)}</div>
                  </div>
                  <Button data-testid={`tv-extra-remove-${i}`} onClick={() => removeTvExtra(i)} variant="outline" className="border-red-900 text-red-400 hover:bg-red-950 rounded-sm">
                    Quitar
                  </Button>
                </CardContent>
              </Card>
            ))}
            <Button data-testid="add-tv-extra-btn" type="button" onClick={addTvExtra} variant="outline" className="border-[#0F766E]/50 text-[#5EEAD4] hover:bg-[#0F766E]/10 rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> Agregar TV LED extra
            </Button>
          </div>
          <div className="text-[11px] text-zinc-500 mt-3">TV LED extra sujeto a disponibilidad.</div>
        </div>

        <Separator className="bg-zinc-900" />

        {/* Upgrades generales */}
        <div>
          <div className="text-base font-semibold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF5A00]" /> Otros upgrades</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {generalUpgrades.map((it) => {
              const q = qtyOf(it.id);
              const Icon = iconFor(it);
              const isSelected = q > 0;
              return (
                <Card key={it.id} className={`upgrade-card relative rounded-sm border ${isSelected ? "upgrade-selected border-[#FF5A00] bg-[#FF5A00]/10" : "border-zinc-800 bg-zinc-900 hover:border-[#FF5A00]/50"}`}>
                  {isSelected && (
                    <span className="upgrade-check absolute top-3 right-3 w-6 h-6 rounded-full bg-[#FF5A00] grid place-items-center z-10">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </span>
                  )}
                  <CardContent className="p-4 flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-9 h-9 rounded-sm grid place-items-center border ${isSelected ? "border-[#FF5A00]/60 bg-[#FF5A00]/15" : "border-zinc-800 bg-zinc-950"}`}>
                        <Icon className={`w-5 h-5 ${isSelected ? "text-[#FF5A00]" : "text-zinc-400"}`} />
                      </div>
                      <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] rounded-sm">{it.category}</Badge>
                    </div>
                    <div className="text-base font-semibold leading-snug">{it.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{it.description}</div>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="text-xl font-mono text-[#FF5A00]">{formatUSD(it.unit_price)}</span>
                      <span className="text-[10px] text-zinc-500 uppercase">+ IVA</span>
                    </div>
                    <div className="mt-auto pt-4">
                      {q === 0 ? (
                        <Button
                          type="button"
                          data-testid={`upgrade-add-${it.id}`}
                          onClick={() => setQty(it, 1)}
                          className="w-full bg-zinc-100 text-zinc-900 hover:bg-[#FF5A00] hover:text-white rounded-sm h-9 text-sm font-semibold transition-colors"
                        >
                          <Plus className="w-4 h-4 mr-1.5" /> Agregar
                        </Button>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1 border border-[#FF5A00]/40 bg-zinc-950 rounded-sm">
                            <button data-testid={`upgrade-dec-${it.id}`} type="button" onClick={() => setQty(it, Math.max(0, q - 1))} className="w-9 h-9 grid place-items-center hover:bg-zinc-800"><Minus className="w-3.5 h-3.5" /></button>
                            <div data-testid={`upgrade-qty-${it.id}`} className="w-9 text-center text-sm font-mono">{q}</div>
                            <button data-testid={`upgrade-inc-${it.id}`} type="button" onClick={() => setQty(it, q + 1)} className="w-9 h-9 grid place-items-center hover:bg-zinc-800"><Plus className="w-3.5 h-3.5" /></button>
                          </div>
                          <button data-testid={`upgrade-remove-${it.id}`} type="button" onClick={() => setQty(it, 0)} className="text-xs text-zinc-500 hover:text-red-400 transition">Quitar</button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <Card className="rounded-sm border border-zinc-800 border-dashed bg-zinc-950 md:col-span-2 lg:col-span-3">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <HelpCircle className="w-4 h-4 text-[#FF5A00]" />
                  <div className="text-sm font-semibold">Otros — pedido especial</div>
                  <Badge variant="outline" className="ml-auto border-zinc-700 text-zinc-400 rounded-sm text-[10px]">a responder</Badge>
                </div>
                <p className="text-xs text-zinc-400 mb-3">
                  Contanos qué adicional necesitás y te responderemos con disponibilidad y cotización. Este campo no suma precio automáticamente.
                </p>
                <Textarea data-testid="upgrade-otros-input" rows={3} className={inputCls} value={otros} onChange={(e) => setOtros(e.target.value)} placeholder="Ej: vitrina baja de exhibición, mostrador impreso..." />
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="sticky bottom-4 z-10">
          <Card className="bg-zinc-950 border-[#FF5A00]/40 rounded-sm shadow-2xl shadow-[#FF5A00]/10">
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF5A00] mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Upgrades seleccionados
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 text-sm flex-1">
                  <Total label="Adicionales" value={String(itemCount)} />
                  <Total label="Subtotal upgrades" value={formatUSD(totals.subtotal)} />
                  <Total label="IVA (21%)" value={formatUSD(totals.iva)} />
                  <Total label="Total pedido" value={formatUSD(totalPedido)} accent />
                </div>
                <Saver onSave={save} saving={saving} label="Guardar upgrades" />
              </div>
              <div className="text-[10px] text-zinc-500 mt-3">
                Total pedido = Paquete Full Equipado ({formatUSD(stand.base_price || 0)}) + upgrades, con IVA incluido. Se actualiza en tiempo real.
              </div>
            </CardContent>
          </Card>
        </div>
      </fieldset>
    </div>
  );
}

const Total = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`font-mono text-lg ${accent ? "text-[#FF5A00]" : "text-white"}`} data-testid={`total-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>{value}</div>
  </div>
);

// -------------------- 6. Pedido final --------------------
export function StepPedido({ stand, reload }) {
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get(`/expositor/stand/${stand.stand_number}/validate`).then((r) => setPendientes(r.data.pendientes || [])).catch(() => {});
    api.get(`/expositor/stand/${stand.stand_number}/pricing`).then((r) => setPricing(r.data)).catch(() => {});
  }, [stand.stand_number]);

  const up = stand.configuration.upgrades || {};
  const items = normalizeCart(up);
  const tvExtras = up.tv_extras || [];
  const upSub = items.reduce((s, c) => s + c.unit_price * c.quantity, 0);
  const tvExtraSub = tvExtras.reduce((s, t) => s + (t.price || 0) * (t.quantity || 0), 0);
  const basePrice = Number(stand.base_price || 0);
  const descuentosTotal = pricing?.descuentos_total || 0;
  const subtotal = Math.max(0, basePrice + upSub + tvExtraSub - descuentosTotal);
  const iva = subtotal * 0.21;
  const total = subtotal + iva;
  const hasUpgrades = items.length > 0 || tvExtras.length > 0;
  const submittable = ["codigo_emitido", "en_carga", "rechazado"].includes(stand.status) || stand.reopened || _adminEditMode();

  function _adminEditMode() {
    try { return !!sessionStorage.getItem("am_admin_edit_mode"); } catch { return false; }
  }

  const submit = async () => {
    if (!confirm) return toast.error("Confirmá para enviar el pedido");
    let motivo = "";
    let adminMode = null;
    try {
      const raw = sessionStorage.getItem("am_admin_edit_mode");
      adminMode = raw ? JSON.parse(raw) : null;
    } catch { adminMode = null; }
    if (adminMode && adminMode.requiresReason) {
      motivo = window.prompt("Esta configuración posee aprobación o estado comercial avanzado.\nIndicá el motivo de la modificación (obligatorio):", "");
      if (!motivo || !motivo.trim()) return toast.error("Motivo obligatorio para modificar un pedido aprobado o pagado.");
    } else if (adminMode && stand.status === "enviado_para_revisar") {
      const ok = window.confirm("Esta configuración ya fue enviada. Las modificaciones quedarán registradas y se notificará a las partes involucradas. ¿Continuar?");
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      await api.post(`/expositor/stand/${stand.stand_number}/submit`, { confirm: true, motivo: motivo.trim() });
      toast.success(adminMode ? "Modificación registrada y notificada correctamente." : "Pedido enviado correctamente. El equipo de Servicios Oficiales revisará la información y confirmará el pedido.");
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && Array.isArray(d.faltantes)) {
        toast.error(`Faltan datos obligatorios: ${d.faltantes.join(", ")}`);
      } else {
        toast.error(typeof d === "string" ? d : "Error al enviar pedido");
      }
    }
    finally { setSubmitting(false); }
  };

  const cfg = stand.configuration;
  const logoLabel = cfg.logo?.link ? "Cargado" : cfg.logo?.requires_assistance ? "Requiere asistencia" : "Pendiente";
  const disenoLabel = cfg.diseno?.opcion === "A" ? "Estándar prearmado" : "Personalizado por panel";
  const tvMap = { A: "Video institucional", B: "Logo animado", C: "Requiere asistencia AV" };

  return (
    <div>
      <SectionTitle
        kicker="Paso 6"
        title="Pedido final"
        desc="Revisá el resumen y enviá tu pedido. Servicios Oficiales lo revisará y confirmará."
      />

      {/* Resumen */}
      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pedido #</div>
              <div className="font-mono text-lg" data-testid="orden-number">{stand.payment?.order_number}</div>
            </div>
            <div className="flex items-center gap-4">
              <img src={BRAND.LOGO_SO} alt="Servicios Oficiales" className="h-6 w-auto invert opacity-70" />
              <Badge variant="outline" className="border-[#FF5A00]/40 text-[#FF5A00] rounded-sm" data-testid="pedido-status-badge">
                {STATUS_LABEL[stand.status]}
              </Badge>
            </div>
          </div>

          {/* Bloque Espacio contratado */}
          <div className="mb-4 p-4 border border-zinc-800 bg-zinc-950 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Espacio ya contratado</div>
            <div className="grid md:grid-cols-4 gap-3 text-sm">
              <SmallRow k="N° stand" v={stand.stand_number} />
              <SmallRow k="Medida" v={`${fmtNum(stand.ancho)} × ${fmtNum(stand.profundidad)} m`} />
              <SmallRow k="Ubicación" v={TIPO_UBICACION_LABEL[stand.tipo_ubicacion] || "—"} />
              <SmallRow k="Superficie" v={`${fmtNum(stand.superficie)} m²`} />
            </div>
          </div>

          {/* Bloque Paquete Full Equipado */}
          <div className="mb-4 p-4 border border-[#FF5A00]/30 bg-[#FF5A00]/5 rounded-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">Paquete Full Equipado</div>
              <div className="font-mono text-xl text-white">USD {basePrice.toLocaleString("es-AR")}<span className="text-xs text-zinc-400 ml-1">+ IVA</span></div>
            </div>
            <div className="text-[11px] text-zinc-400">Incluye gráfica, TV LED 42″, mobiliario, alfombra, iluminación y logo corpóreo.</div>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <Summary label="Empresa" value={stand.company} />
            <Summary label="Logo" value={logoLabel} />
            <Summary label="Diseño" value={disenoLabel} />
            <Summary label="Color LED" value={cfg.terminaciones?.led_color} />
            <Summary label="Color alfombra" value={cfg.terminaciones?.alfombra_color} />
            <Summary label="Contenido TV" value={tvMap[cfg.tv?.opcion] || "—"} />
            <Summary label="Facturación" value={cfg.datos_facturacion?.razon_social || "—"} />
          </div>

          {hasUpgrades ? (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 mt-4">Upgrades seleccionados</div>
              <div className="border border-zinc-800 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-950 text-zinc-500 text-[10px] uppercase tracking-wider">
                    <tr><th className="text-left p-3">Item</th><th className="text-right p-3">Cant.</th><th className="text-right p-3">P.U. USD</th><th className="text-right p-3">Subtotal</th></tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.upgrade_id} className="border-t border-zinc-800">
                        <td className="p-3">{it.name}</td>
                        <td className="p-3 text-right font-mono">{it.quantity}</td>
                        <td className="p-3 text-right font-mono">{formatUSD(it.unit_price)}</td>
                        <td className="p-3 text-right font-mono">{formatUSD(it.unit_price * it.quantity)}</td>
                      </tr>
                    ))}
                    {tvExtras.map((t, i) => (
                      <tr key={`tvx-${i}`} className="border-t border-zinc-800 bg-[#0F766E]/5">
                        <td className="p-3">TV LED extra {t.size}</td>
                        <td className="p-3 text-right font-mono">{t.quantity}</td>
                        <td className="p-3 text-right font-mono">{formatUSD(t.price)}</td>
                        <td className="p-3 text-right font-mono">{formatUSD(t.price * t.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-sm text-zinc-400 mt-2">No seleccionaste upgrades. Sólo el Paquete Full Equipado base.</div>
          )}

          {/* Totales */}
          <div className="mt-6 border-t border-zinc-800 pt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Paquete base</div>
                <div className="font-mono text-white">{formatUSD(basePrice)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Upgrades</div>
                <div className="font-mono text-white">{formatUSD(upSub)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">TV LED extra</div>
                <div className="font-mono text-white">{formatUSD(tvExtraSub)}</div>
              </div>
              {descuentosTotal > 0 && (
                <div data-testid="pedido-desc-row">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-400">Descuentos incluidos</div>
                  <div className="font-mono text-emerald-300">− {formatUSD(descuentosTotal)}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">IVA (21%)</div>
                <div className="font-mono text-white">{formatUSD(iva)}</div>
              </div>
            </div>
            <div className="mt-4 flex items-baseline justify-between border-t border-zinc-800 pt-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">Total en USD</div>
              <div className="font-mono text-2xl text-[#FF5A00]" data-testid="pedido-total">{formatUSD(total)}</div>
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">Los valores están expresados en dólares estadounidenses + IVA.</div>
          </div>

          <Alert className="bg-amber-500/5 border-amber-500/30 rounded-sm mt-4">
            <AlertDescription className="text-xs text-amber-200 leading-relaxed">
              Este pedido corresponde únicamente al Paquete Full Equipado y upgrades seleccionados.
              <b> No incluye el valor del espacio</b>, ya que fue contratado previamente con la organización.
            </AlertDescription>
          </Alert>

          {up.otros_request && (
            <div className="mt-4 p-3 border border-dashed border-zinc-700 rounded-sm bg-zinc-950">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Pedido especial (Otros)</div>
              <div className="text-sm text-zinc-200">{up.otros_request}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pendientes informativos */}
      {pendientes.length > 0 && (
        <Alert className="bg-zinc-900 border-zinc-800 rounded-sm mt-6">
          <AlertDescription className="text-sm text-zinc-300">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Información pendiente de completar</div>
            <ul className="list-disc list-inside space-y-0.5">
              {pendientes.map((p) => <li key={p} className="text-zinc-300">{p}</li>)}
            </ul>
            <div className="mt-3 text-xs text-zinc-500">
              Podés enviar el pedido con información pendiente. Recordá completar los datos faltantes cuanto antes para evitar demoras.
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Confirmación final */}
      <div className="mt-8 p-5 border border-zinc-800 rounded-sm bg-zinc-900/50">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox data-testid="confirm-checkbox" checked={confirm} onCheckedChange={(c) => setConfirm(!!c)} className="mt-0.5" disabled={!submittable} />
          <span className="text-sm leading-relaxed">
            Confirmo que la información cargada es correcta y deseo enviar el pedido sujeto a confirmación.
          </span>
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          data-testid="submit-pedido-btn"
          onClick={submit}
          disabled={!confirm || submitting || !submittable}
          className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-11 px-6"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Enviar pedido a confirmar</>}
        </Button>
        {stand.submitted_at && (
          <a
            data-testid="download-pdf-btn"
            href={`${API}/expositor/stand/${stand.stand_number}/pedido.pdf${getExpositorToken() ? `?token=${encodeURIComponent(getExpositorToken())}` : ""}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center h-11 px-5 border border-zinc-700 hover:bg-zinc-900 rounded-sm text-sm text-zinc-200 transition"
          >
            <FileDown className="w-4 h-4 mr-2 text-[#FF5A00]" /> Descargar resumen (PDF)
          </a>
        )}
      </div>
      <div className="mt-2">
        {!submittable && (
          <div className="text-xs text-zinc-500 mt-3 flex items-center">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-emerald-500" /> Pedido ya enviado · estado actual: {STATUS_LABEL[stand.status] || stand.status}
          </div>
        )}
      </div>
    </div>
  );
}

const Summary = ({ label, value }) => (
  <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-3">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
    <div className="text-sm text-zinc-100">{value || "—"}</div>
  </div>
);

const SmallRow = ({ k, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
    <div className="text-sm text-zinc-100 font-mono">{v || "—"}</div>
  </div>
);

const Row = ({ k, v, mono }) => (
  <div className="flex justify-between gap-3 border-b border-zinc-900 pb-1">
    <dt className="text-zinc-500">{k}</dt>
    <dd className={`text-zinc-200 text-right ${mono ? "font-mono" : ""}`}>{v}</dd>
  </div>
);
